import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus, Calendar } from "lucide-react";
import { cn } from "../../../lib/utils";
import { CheckboxState } from "../sammelrechnungen-table.types";

interface SammelrechnungenCheckboxProps {
	state: CheckboxState;
	onChange: (checked: boolean) => void;
	className?: string;
	disabled?: boolean;
}

export const SammelrechnungenCheckbox: React.FC<SammelrechnungenCheckboxProps> = ({
	state,
	onChange,
	className,
	disabled = false,
}) => {
	const getCheckboxProps = () => {
		switch (state) {
			case CheckboxState.EMPTY:
				return {
					checked: false,
					indeterminate: false,
					icon: null,
				};
			case CheckboxState.FULL:
				return {
					checked: true,
					indeterminate: false,
					icon: <Check className="h-4 w-4" />,
				};
			case CheckboxState.PARTIAL:
				return {
					checked: false,
					indeterminate: true,
					icon: <Minus className="h-4 w-4" />,
				};
			case CheckboxState.DATE_FILTERED:
				return {
					checked: true,
					indeterminate: false,
					icon: <Calendar className="h-4 w-4" />,
				};
		}
	};

	const { checked, indeterminate, icon } = getCheckboxProps();

	const getStateColor = () => {
		switch (state) {
			case CheckboxState.EMPTY:
				return "";
			case CheckboxState.FULL:
				return "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground";
			case CheckboxState.PARTIAL:
				return "data-[state=indeterminate]:bg-yellow-500 data-[state=indeterminate]:text-white";
			case CheckboxState.DATE_FILTERED:
				return "data-[state=checked]:bg-blue-500 data-[state=checked]:text-white";
		}
	};

	const handleCheckedChange = (checkedValue: boolean | "indeterminate") => {
		// Convert indeterminate to boolean for our callback
		onChange(checkedValue === true);
	};

	const handleClick = (e: React.MouseEvent) => {
		// Prevent the click from bubbling up to parent elements (like table row)
		e.stopPropagation();
	};

	return (
		<CheckboxPrimitive.Root
			checked={indeterminate ? "indeterminate" : checked}
			onCheckedChange={handleCheckedChange}
			onClick={handleClick}
			disabled={disabled}
			className={cn(
				"peer h-5 w-5 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
				getStateColor(),
				className
			)}
		>
			<CheckboxPrimitive.Indicator
				className={cn("flex items-center justify-center text-current")}
			>
				{icon}
			</CheckboxPrimitive.Indicator>
		</CheckboxPrimitive.Root>
	);
};